import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getSelectedTab } from '../selectors/tabsSelectors';
import { getDealGroupingTypeFilter, getDealStageFilter, getPipelineFilter } from '../selectors/filtersSelectors';
import { getPipelines } from '../selectors/forecastSelectors';
import { TABS } from '../constants/Tabs';
import { FILTERS } from '../constants/FiltersConstants';
import { DEAL_STAGE } from '../constants/DealGroupingType';
import { ALL_PIPELINES } from '../constants/Pipelines';
import { setFilter } from '../actions/FiltersActions';

//  Hook that coordinates filter dependencies to ensure they are in sync
export const useFilterCoordination = () => {
  const dispatch = useDispatch();
  const selectedTab = useSelector(getSelectedTab);
  const pipelines = useSelector(getPipelines);
  const rawPipelineFilter = useSelector(getPipelineFilter);
  const pipelinesFilter = useMemo(() => (rawPipelineFilter === null || rawPipelineFilter === void 0 ? void 0 : rawPipelineFilter.toArray()) || [], [rawPipelineFilter]);
  const prevPipelineFilterRef = useRef(pipelinesFilter);
  const dealGroupingType = useSelector(getDealGroupingTypeFilter);
  const dealStageGroupings = useSelector(getDealStageFilter);
  const location = useLocation();
  const isDetail = location.pathname.includes('/owner/');

  // Handle tab changes and their effect on pipeline filter
  useEffect(() => {
    if (!pipelines.size || !pipelinesFilter.length) return;

    // For the analyze tab, we expliticly set the pipeline filter to ALL_PIPELINES
    // instead of selecting each and every pipeline like we do on the Forecast category view
    if (selectedTab === TABS.ANALYZE) {
      const newFilter = pipelines.size === pipelinesFilter.length ? [ALL_PIPELINES] : [pipelinesFilter[0]];
      if (newFilter.toString() !== pipelinesFilter.toString()) {
        void dispatch(setFilter({
          filter: FILTERS.PIPELINE,
          value: newFilter
        }));
      }
      // When switching back to the overview from the analyze tab, we:
      // select the first pipeline (if deal stage view) or
      // select each and every pipeline (if forecast category view)
    } else if (pipelinesFilter[0] === ALL_PIPELINES) {
      const newFilter = dealGroupingType === DEAL_STAGE ? [pipelines.first().get('pipelineId')] : pipelines.map(val => val.get('pipelineId'));
      if (newFilter.toString() !== pipelinesFilter.toString()) {
        void dispatch(setFilter({
          filter: FILTERS.PIPELINE,
          value: newFilter
        }));
      }
    }
  }, [pipelines, pipelinesFilter, dealGroupingType, dispatch, selectedTab]);

  // Handle deal grouping type changes and their effect on pipeline filter
  useEffect(() => {
    if (!pipelines.size || !pipelinesFilter.length) return;

    // When switching to DEAL_STAGE, ensure only one pipeline is selected
    if (dealGroupingType === DEAL_STAGE && pipelinesFilter.length > 1) {
      const newFilter = [pipelinesFilter[0]];
      if (newFilter.toString() !== pipelinesFilter.toString()) {
        void dispatch(setFilter({
          filter: FILTERS.PIPELINE,
          value: newFilter
        }));
      }
    }
  }, [dealGroupingType, pipelinesFilter, dispatch, pipelines.size]);

  // Handle pipeline changes and their effect on deal stage groupings
  useEffect(() => {
    // When changing pipeline selections in deal stage view on detail page,
    // we should clear the deal stage groupings filter
    if (dealGroupingType === DEAL_STAGE && isDetail && pipelinesFilter.length && dealStageGroupings !== null && dealStageGroupings !== void 0 && dealStageGroupings.size) {
      // Compare with the actual previous value from our ref
      if (prevPipelineFilterRef.current.length === 1 && pipelinesFilter.length === 1 && prevPipelineFilterRef.current[0] !== pipelinesFilter[0]) {
        void dispatch(setFilter({
          filter: FILTERS.DEAL_STAGE_GROUPINGS,
          value: []
        }));
      }

      // Update the ref with the current value for the next comparison
      prevPipelineFilterRef.current = pipelinesFilter;
    }
  }, [dealGroupingType, isDetail, pipelinesFilter, dealStageGroupings === null || dealStageGroupings === void 0 ? void 0 : dealStageGroupings.size, dispatch]);
};